import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  ClipboardCopy,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Form,
  FormGroup,
  FormGroupLabelHelp,
  FormHelperText,
  FormSelect,
  FormSelectOption,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Popover,
  Spinner,
  TextInput,
} from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import type { DkimKeyCreateResult } from '../../../types/models/DkimKey';
import type { MailDomainDnsZoneState } from '../../../types/models/MailDomain';
import type { ApiResponseDkimKey } from '../../../types/api/ApiResponseDkimKey';
import type { ApiResponseMailDomainDnsZone } from '../../../types/api/ApiResponseMailDomain';
import type { ApiResponseDnsRrsetWrite } from '../../../types/api/ApiResponseDomainName';

interface DkimCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  mailDomainId: number;
  /** Bridge to the DNS side — the dns-zone route is keyed by this (lets us offer one-click publish). */
  organizationDomainId: number;
  domainName: string;
  /** Refreshes the selectors table after a key is created. */
  onSuccess: () => void;
}

const SELECTOR_REGEX = /^[a-zA-Z0-9._-]+$/;
const DEFAULT_SELECTOR = 'ancalagon';
const DEFAULT_BITS = '2048';
const KEY_SIZES = ['1024', '2048', '4096'];

function DkimCreateModal({
  isOpen,
  onClose,
  mailDomainId,
  organizationDomainId,
  domainName,
  onSuccess,
}: DkimCreateModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();

  const [selector, setSelector] = useState(DEFAULT_SELECTOR);
  const [bits, setBits] = useState(DEFAULT_BITS);
  const [force, setForce] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DkimKeyCreateResult | null>(null);

  // DNS zone state for the domain (fetched on open, in parallel with the form) — drives whether we can
  // offer to publish the created record automatically.
  const [dnsZone, setDnsZone] = useState<MailDomainDnsZoneState | null>(null);
  const [dnsZoneLoading, setDnsZoneLoading] = useState(false);

  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [published, setPublished] = useState(false);

  const trimmedSelector = selector.trim();
  const isSelectorValid = SELECTOR_REGEX.test(trimmedSelector);

  // We can auto-publish only when the domain's zone already exists on a managed endpoint; the other
  // states (not_configured / unknown / unmanaged / probe error) fall back to the manual copy display.
  const canAutoPublish = dnsZone !== null && dnsZone.zone_state === 'configured' && dnsZone.domain_name_id !== null;

  const fetchDnsZone = useCallback(async () => {
    setDnsZoneLoading(true);
    try {
      const response = await apiFetch(`/emails/domains/dns-zone?organization_domain_id=${organizationDomainId}`);
      const body = (await response.json().catch(() => null)) as ApiResponseMailDomainDnsZone | null;
      if (!response.ok || body?.status !== 'success' || !body.data?.dns_zone) {
        setDnsZone(null);
        return;
      }
      setDnsZone(body.data.dns_zone);
    } catch {
      setDnsZone(null);
    } finally {
      setDnsZoneLoading(false);
    }
  }, [apiFetch, organizationDomainId]);

  // Probe the zone state whenever the modal opens, so the result phase can offer one-click publish.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setPublished(false);
    setPublishError(null);
    void fetchDnsZone();
  }, [isOpen, fetchDnsZone]);

  const resetAndClose = () => {
    setSelector(DEFAULT_SELECTOR);
    setBits(DEFAULT_BITS);
    setForce(false);
    setError(null);
    setResult(null);
    setIsSubmitting(false);
    setDnsZone(null);
    setDnsZoneLoading(false);
    setPublishing(false);
    setPublishError(null);
    setPublished(false);
    onClose();
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await apiFetch(`/emails/domains/${mailDomainId}/dkim`, {
        method: 'POST',
        body: JSON.stringify({ selector: trimmedSelector, bits: Number(bits), force }),
      });

      // The DKIM endpoints map controller exceptions to JSend `status: 'error'` at HTTP 200, so we branch
      // on body.status, not response.ok (an existing key returns an error message — retry with "force" on).
      const body = (await response.json()) as ApiResponseDkimKey;
      if (body.status === 'success' && body.data?.dkim_key) {
        setResult(body.data.dkim_key);
        addNotification({
          title: 'DKIM key created',
          srTitle: 'Success alert',
          description: `A DKIM key for selector "${body.data.dkim_key.selector}" was created on ${domainName}. Publish the DNS record to activate it.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        onSuccess();
      } else {
        throw new Error(body.message ?? 'Failed to create DKIM key');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setError(message);
      addNotification({
        title: 'DKIM key creation failed',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePublish = async () => {
    if (!result || dnsZone === null || dnsZone.domain_name_id === null) {
      return;
    }
    const domainNameId = dnsZone.domain_name_id;
    setPublishing(true);
    setPublishError(null);

    // The rrset endpoint quotes/escapes/255-octet-chunks TXT content server-side, so the long DKIM value
    // is sent as a single raw string.
    const payload = { name: result.dns_record_name, type: 'TXT', ttl: 3600, content: [result.dns_record_value] };
    const send = (method: 'POST' | 'PATCH') =>
      apiFetch(`/datacenters/domain-names/${domainNameId}/dns/rrset`, {
        method,
        headers: { Accept: 'application/json' },
        body: JSON.stringify(payload),
      });

    try {
      let response = await send('POST');
      let body = (await response.json().catch(() => null)) as ApiResponseDnsRrsetWrite | null;

      // Collision fallback: an existing TXT set at this name (e.g. re-publishing after rotating the same
      // selector) is replaced in place via PATCH rather than failing.
      const collided =
        (!response.ok || body?.status !== 'success') &&
        typeof body?.data?.name === 'string' &&
        body.data.name.toLowerCase().includes('already exists');
      if (collided) {
        response = await send('PATCH');
        body = (await response.json().catch(() => null)) as ApiResponseDnsRrsetWrite | null;
      }

      if (response.ok && body?.status === 'success' && body.data?.record_set) {
        setPublished(true);
        addNotification({
          title: 'DKIM DNS record published',
          srTitle: 'Success alert',
          description: `The DKIM TXT record for selector "${result.selector}" was published to ${domainName}'s DNS zone.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        return;
      }

      const reason =
        (typeof body?.data?.name === 'string' ? body.data.name : null) ??
        body?.message ??
        `Request failed with status ${response.status}`;
      throw new Error(reason);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setPublishError(message);
      addNotification({
        title: 'DNS publish failed',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setPublishing(false);
    }
  };

  const renderForm = () => (
    <>
      {error && (
        <Alert variant="danger" isInline title="Creation failed" className="pf-v6-u-mb-md">
          {error}
        </Alert>
      )}
      <Alert variant="info" isInline title="Publish the DNS record after creating" className="pf-v6-u-mb-md">
        This generates a DKIM signing key on the mail server for <strong>{domainName}</strong>. Once it&apos;s created,
        publish the returned DNS TXT record in the domain&apos;s zone so receiving servers can verify your mail
        {canAutoPublish ? ' — since this domain’s DNS is managed here, you’ll be able to publish it in one click.' : '.'}
      </Alert>
      <Form>
        <FormGroup
          label="Selector"
          isRequired
          fieldId="dkim-selector"
          labelHelp={
            <Popover
              headerContent="DKIM selector"
              bodyContent={
                <>
                  The selector names this key within the domain and becomes part of the DNS record name — e.g. selector{' '}
                  <code>{trimmedSelector || DEFAULT_SELECTOR}</code> publishes to{' '}
                  <code>
                    {trimmedSelector || DEFAULT_SELECTOR}._domainkey.{domainName}
                  </code>
                  . Use a fresh selector to rotate keys without downtime.
                </>
              }
            >
              <FormGroupLabelHelp aria-label="DKIM selector help" />
            </Popover>
          }
        >
          <TextInput
            id="dkim-selector"
            value={selector}
            onChange={(_event, value) => setSelector(value)}
            validated={selector && !isSelectorValid ? 'error' : 'default'}
          />
          {selector && !isSelectorValid ? (
            <FormHelperText>
              <HelperText>
                <HelperTextItem variant="error">
                  Only letters, numbers, dots, hyphens, and underscores are allowed.
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          ) : (
            trimmedSelector.length > 0 && (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem>
                    DNS record name: <strong>{trimmedSelector}._domainkey.{domainName}</strong>
                  </HelperTextItem>
                </HelperText>
              </FormHelperText>
            )
          )}
        </FormGroup>

        <FormGroup
          label="Key size"
          fieldId="dkim-bits"
          labelHelp={
            <Popover
              headerContent="Key size"
              bodyContent="Larger keys are stronger but produce a longer DNS record. 2048 bits is the recommended default; some DNS providers can't store a 4096-bit record in a single TXT string."
            >
              <FormGroupLabelHelp aria-label="Key size help" />
            </Popover>
          }
        >
          <FormSelect
            id="dkim-bits"
            value={bits}
            onChange={(_event, value) => setBits(value)}
            aria-label="DKIM key size"
          >
            {KEY_SIZES.map((size) => (
              <FormSelectOption key={size} value={size} label={`${size} bits`} />
            ))}
          </FormSelect>
        </FormGroup>

        <Checkbox
          id="dkim-force"
          label="Replace the existing key for this selector"
          description="Regenerate (rotate) the key if one already exists for this selector. The old key stops signing immediately."
          isChecked={force}
          onChange={(_event, checked) => setForce(checked)}
        />
      </Form>
    </>
  );

  const renderPublishSection = (key: DkimKeyCreateResult) => {
    if (published) {
      return null;
    }
    if (dnsZoneLoading) {
      return (
        <div className="pf-v6-u-mt-md">
          <Content component="small">
            <Spinner size="sm" aria-label="Checking DNS zone" /> Checking whether this domain&apos;s DNS is managed
            here…
          </Content>
        </div>
      );
    }
    if (canAutoPublish) {
      return (
        <div className="pf-v6-u-mt-md">
          {publishError && (
            <Alert variant="warning" isInline title="Couldn't publish automatically" className="pf-v6-u-mb-sm">
              {publishError} You can still copy the record above and add it at your DNS provider.
            </Alert>
          )}
          <Button variant="primary" onClick={handlePublish} isLoading={publishing} isDisabled={publishing}>
            Publish to {domainName}&apos;s DNS zone
          </Button>
        </div>
      );
    }
    // Not auto-publishable — either the zone isn't managed here, or the probe couldn't determine it
    // (dnsZone === null after a failed/empty fetch). Always point at the manual record above; offer a
    // retry when the probe itself failed.
    return (
      <div className="pf-v6-u-mt-md">
        <Content component="small">
          {dnsZone ? (
            <>
              This domain&apos;s DNS isn&apos;t managed here, so add the <strong>{key.dns_record_type}</strong> record
              above at your DNS provider.
            </>
          ) : (
            <>
              We couldn&apos;t check whether this domain&apos;s DNS is managed here — add the{' '}
              <strong>{key.dns_record_type}</strong> record above at your DNS provider, or{' '}
              <Button variant="link" isInline onClick={() => void fetchDnsZone()}>
                retry the check
              </Button>
              .
            </>
          )}
        </Content>
      </div>
    );
  };

  const renderResult = (key: DkimKeyCreateResult) => (
    <>
      {published ? (
        <Alert variant="success" isInline title="DKIM key created and published" className="pf-v6-u-mb-md">
          The DNS TXT record was published to <strong>{domainName}</strong>&apos;s zone. DKIM will verify once it
          propagates.
        </Alert>
      ) : (
        <Alert variant="success" isInline title="DKIM key created" className="pf-v6-u-mb-md">
          Publish the TXT record below in <strong>{domainName}</strong>&apos;s DNS zone. Until it resolves, DKIM
          signatures can&apos;t be verified by receiving servers.
        </Alert>
      )}
      {key.service_restart_required && !key.service_restarted && (
        <Alert variant="warning" isInline title="Signing not active yet" className="pf-v6-u-mb-md">
          The mail server still needs to reload OpenDKIM before this key signs outgoing mail.
        </Alert>
      )}
      <DescriptionList isHorizontal>
        <DescriptionListGroup>
          <DescriptionListTerm>Selector</DescriptionListTerm>
          <DescriptionListDescription>{key.selector}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Record name</DescriptionListTerm>
          <DescriptionListDescription>
            <ClipboardCopy isReadOnly hoverTip="Copy" clickTip="Copied" textAriaLabel="DKIM record name">
              {key.dns_record_name}
            </ClipboardCopy>
          </DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Type</DescriptionListTerm>
          <DescriptionListDescription>{key.dns_record_type}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Value</DescriptionListTerm>
          <DescriptionListDescription>
            <ClipboardCopy
              isReadOnly
              isCode
              variant="expansion"
              hoverTip="Copy"
              clickTip="Copied"
              textAriaLabel="DKIM record value"
              toggleAriaLabel="Show the full DKIM record value"
            >
              {key.dns_record_value}
            </ClipboardCopy>
          </DescriptionListDescription>
        </DescriptionListGroup>
      </DescriptionList>
      {renderPublishSection(key)}
      <div className="pf-v6-u-mt-md">
        <Content component="small">
          The key is {key.key_bits}-bit{key.forced ? ' and replaced a previous key for this selector.' : '.'}
        </Content>
      </div>
    </>
  );

  return (
    <Modal variant="medium" isOpen={isOpen} onClose={resetAndClose} aria-label="Create DKIM configuration">
      <ModalHeader title="Create DKIM configuration" />
      <ModalBody>{result ? renderResult(result) : renderForm()}</ModalBody>
      <ModalFooter>
        {result ? (
          <Button variant="primary" onClick={resetAndClose}>
            Done
          </Button>
        ) : (
          <>
            <Button
              variant="primary"
              onClick={handleSubmit}
              isDisabled={!isSelectorValid || isSubmitting}
              isLoading={isSubmitting}
            >
              Create DKIM key
            </Button>
            <Button variant="link" onClick={resetAndClose}>
              Cancel
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
}

export default DkimCreateModal;
